import { useState, useRef, useEffect, useCallback } from 'react'
import { fmtUnit } from '@/lib/format-unit'
import type { PricingItem } from '@/types'

const SearchIcon = () => (
  <svg style={{ width: 16, height: 16 }} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
)

const PlusIcon = () => (
  <svg style={{ width: 18, height: 18 }} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const CheckIcon = () => (
  <svg style={{ width: 12, height: 12 }} fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

interface ItemPickerDropdownProps {
  items: PricingItem[]
  onSelect: (item: PricingItem) => Promise<void> | void
  buttonLabel?: string
  isPublicWorks?: boolean
  serviceFilter?: string
}

export function ItemPickerDropdown({ items, onSelect, buttonLabel = 'Add Item', isPublicWorks, serviceFilter }: ItemPickerDropdownProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [flipUp, setFlipUp] = useState(false)
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState<number | undefined>(undefined)
  const [isAdding, setIsAdding] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
        setSelected(new Set())
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  // Compute available space above/below button and constrain dropdown height
  useEffect(() => {
    if (!open || !ref.current) return
    const btnRect = ref.current.getBoundingClientRect()
    const vh = window.innerHeight
    const pad = 12
    const spaceDown = vh - btnRect.top - pad
    const spaceUp = btnRect.bottom - pad
    if (spaceDown >= spaceUp) {
      setFlipUp(false)
      setDropdownMaxHeight(spaceDown)
    } else {
      setFlipUp(true)
      setDropdownMaxHeight(spaceUp)
    }
  }, [open])

  const handleOpen = useCallback(() => {
    setFlipUp(false)
    setDropdownMaxHeight(undefined)
    setOpen((prev) => !prev)
  }, [])

  const filtered = items.filter((i) =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    i.service?.toLowerCase().includes(search.toLowerCase())
  )

  const fmt = (v: number) => `$${v.toFixed(2)}`

  const toggleItem = (itemId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }

  const handleAddSelected = async () => {
    const selectedItems = items.filter((i) => selected.has(i.id))
    setOpen(false)
    setSearch('')
    setSelected(new Set())
    setIsAdding(true)
    try {
      await Promise.all(selectedItems.map((item) => Promise.resolve(onSelect(item))))
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className={`epk-wrap${open ? ' epk-wrap-open' : ''}`} ref={ref}>
      <button className="epk-btn" onClick={handleOpen} disabled={isAdding} style={isAdding ? { opacity: 0.7, cursor: 'default' } : undefined}>
        <PlusIcon />
        <span>{isAdding ? 'Adding...' : buttonLabel}</span>
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className={`epk-dropdown${flipUp ? ' epk-dropdown-up' : ''}`}
          style={dropdownMaxHeight !== undefined ? { maxHeight: dropdownMaxHeight } : undefined}
        >
          <div className="epk-search-wrap">
            <SearchIcon />
            <input
              className="epk-search"
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          <div className="epk-list">
            {filtered.length === 0 ? (
              <div className="epk-empty">No items found</div>
            ) : (
              filtered.map((item) => (
                <button
                  key={item.id}
                  className={`epk-item${selected.has(item.id) ? ' epk-item-selected' : ''}`}
                  onClick={() => toggleItem(item.id)}
                >
                  <div className={`epk-checkbox${selected.has(item.id) ? ' epk-checkbox-checked' : ''}`}>
                    {selected.has(item.id) && <CheckIcon />}
                  </div>
                  <div className="epk-item-left">
                    <span className="epk-item-name">{item.name}</span>
                    {item.service && <span className="epk-item-service">{item.service}</span>}
                  </div>
                  <span className="epk-item-meta">
                    {item.unit && <span className="epk-item-unit">{fmtUnit(item.unit)}</span>}
                    <span className="epk-item-rate">{fmt((() => {
                      const svcRates = serviceFilter && item.serviceRates?.[serviceFilter]
                      if (isPublicWorks) {
                        const pw = svcRates ? svcRates.publicWorksRate : item.publicWorksRate
                        if (pw) return pw
                      }
                      return svcRates ? svcRates.rate : item.rate
                    })())}</span>
                  </span>
                </button>
              ))
            )}
          </div>

          {selected.size > 0 && (
            <div className="epk-footer">
              <button className="epk-add-btn" onClick={handleAddSelected}>
                Add Selected ({selected.size})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
